import { ECardinality, IActionDefinitionDto, IDatasourceDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEditorHeader from "../../ui-kit/kuika-editor-header/kuika-editor-header";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSmallMediumText from "../../ui-kit/typography/SmallMediumText/SmallMediumText";
import { requestDatasourceReveal } from "./datasource-reveal";
import { SapActionSwitcherChip } from "./editor-switcher/sap-action-switcher-chip";
import { ISapFileContentOverride, isSapParamList, SapFileContentField, SapSignatureGrid } from "./sap-signature-grid";
import "./sap-action-ui.scss";
import "./editor-bar-surface.scss";

export interface IEditSapActionProps {
  actionDatasource: IDatasourceDto;
  actionId: string;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

export interface IEditSapActionState {
  /** Every fetch this panel makes, not just the commit — the metadata lookups share it. */
  isLoading: boolean;
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  action?: IActionDefinitionDto;
  functionGroups: { label: string; value: string }[];
  functions: { label: string; value: string }[];
  selectedFunctionGroup?: string;
  selectedFunction?: string;
  functionSignatureJson?: any;
  isSearchingGroups?: boolean;
  isSearchingFunctions?: boolean;
  fileContentOverrides?: Record<string, ISapFileContentOverride>;
}

class EditSapAction extends PureSdComponent<IEditSapActionProps & IWithSDProps, IEditSapActionState> {
  formRef = React.createRef<FormInstance>();

  private fgSearchTimer: any;

  private fnSearchTimer: any;

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IEditSapActionProps & IWithSDProps) {
    super(props);
    this.state = {
      isLoading: false,
      saveStatus: "idle",
      functionGroups: [],
      functions: []
    };
  }

  private normalizeFunctionName = (name?: string): string | undefined => name;

  /**
   * The SAP actions of this datasource. Picking one is parked as the pending close, the way
   * the left tree does it, so whatever guard the editor grows later gets its say.
   */
  /** A SAP action is named after the function it calls, so the bar offers no pencil. */
  private renderTitle = (): React.ReactNode => (
    <KuikaEditorTitle
      entityLabel="SAP action"
      canRename={false}
      identity={
        <SapActionSwitcherChip
          sd={this.props.sd}
          setNextCallbackFunction={this.props.setNextCallbackFunction}
          datasource={this.props.actionDatasource}
          currentActionId={this.props.actionId}
          currentName={this.state.action?.name || "SAP Action"}
          onManageAll={this.closePanel}
        />
      }
    />
  );

  closePanel = () => {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  };

  async componentDidMount() {
    await this.loadActionDetail();
    await this.prefillSelectionsFromAction();
    await this.loadSignatureFromHelper();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    if (this.fgSearchTimer) clearTimeout(this.fgSearchTimer);
    if (this.fnSearchTimer) clearTimeout(this.fnSearchTimer);
  }

  private async loadActionDetail() {
    this.setState({ isLoading: true });
    try {
      const res = await DatasourceService.GetActionDetail(this.props.actionId);
      const action = res?.data as IActionDefinitionDto;
      this.setState({ action });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private async prefillSelectionsFromAction() {
    if (!this.state.action) return;
    const sapActionInfo = this.state.action.sapActionInfo;
    let parsed: any | undefined;
    try {
      parsed = typeof sapActionInfo === "string" ? JSON.parse(sapActionInfo) : sapActionInfo;
    } catch {}
    const funcName = parsed?.Function || this.state.action.name;
    const groupName = parsed?.FunctionGroup || parsed?.Group || parsed?.FunctionGroupName || parsed?.GroupName;
    if (groupName) {
      this.formRef.current?.setFieldsValue({ sapFunctionGroup: groupName });
      this.setState({
        selectedFunctionGroup: groupName,
        functionGroups: [{ label: groupName, value: groupName }]
      });
    }
    if (funcName) {
      this.formRef.current?.setFieldsValue({ sapFunction: funcName });
      this.setState({
        selectedFunction: funcName,
        functions: [{ label: funcName, value: funcName }]
      });
    }

    // Mevcut ParameterConfig'ten filename/MIME override'larını state'e prefill et.
    const cfg = parsed?.ParameterConfig;
    if (cfg && typeof cfg === "object") {
      const overrides: { [k: string]: ISapFileContentOverride } = {};
      for (const name of Object.keys(cfg)) {
        const entry = cfg[name];
        if (!entry || typeof entry !== "object") continue;
        overrides[name] = {
          fileNameTemplate: typeof entry.fileNameTemplate === "string" ? entry.fileNameTemplate : undefined,
          mimeType: typeof entry.mimeType === "string" ? entry.mimeType : undefined
        };
      }
      if (Object.keys(overrides).length > 0) {
        this.setState({ fileContentOverrides: overrides });
      }
    }
  }

  private async loadSignatureFromHelper() {
    const dsId = this.props.actionDatasource?.id;
    const funcName =
      this.formRef.current?.getFieldValue("sapFunction") || this.state.selectedFunction || this.state.action?.name;
    if (!dsId || !funcName) return;
    this.setState({ isLoading: true });
    try {
      const res = await DatasourceService.GetSapFunctionSignature(dsId, funcName);
      const raw = res?.data;
      // Try to parse signature to extract FunctionGroup and Function
      let parsed: any = null;
      try {
        parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch {}
      const normalized = Array.isArray(parsed) ? parsed[0] : parsed;
      const groupFromSig = normalized?.FunctionGroup;
      const functionFromSig = normalized?.Function;

      // Update form fields for visibility even if inputs are disabled
      if (groupFromSig) {
        this.formRef.current?.setFieldsValue({ sapFunctionGroup: groupFromSig });
      }
      if (functionFromSig) {
        this.formRef.current?.setFieldsValue({ sapFunction: functionFromSig });
      }

      // Update component state so KuikaSelectbox reflects values
      this.setState((prev) => ({
        functionSignatureJson: raw,
        selectedFunctionGroup: groupFromSig || prev.selectedFunctionGroup,
        functionGroups: groupFromSig ? [{ label: groupFromSig, value: groupFromSig }] : prev.functionGroups,
        selectedFunction: functionFromSig || prev.selectedFunction,
        functions:
          functionFromSig && (!prev.functions || prev.functions.length === 0)
            ? [{ label: functionFromSig, value: functionFromSig }]
            : prev.functions
      }));
    } catch {
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private mapCsTypeToKuika(typeName?: string, prmName?: string): any {
    if (!typeName) return "String";
    const tp = (typeName || "").split(".").pop()!.toLowerCase();
    if (typeName === "void") return "void";
    if (tp === "string") return "String";
    if (tp === "int" || tp === "int32") return "Int32";
    if (tp === "long" || tp === "int64") return "Int64";
    if (tp === "byte") return "Byte";
    if (tp === "bool" || tp === "boolean") return "Boolean";
    if (tp === "char" || tp === "character") return "Character";
    if (tp === "datetime") return "DateTime";
    if (tp === "datetimeoffset") return "DateTimeOffset";
    if (tp === "decimal") return "Decimal";
    if (tp === "double") return "Double";
    if (tp === "guid") return "Guid";
    if (tp === "short" || tp === "int16") return "Int16";
    if (tp === "float") return "Float";
    if (tp === "object") return prmName && (prmName === "clientId" || prmName === "clientSecret") ? "String" : "object";
    return "String";
  }

  private buildParametersFromSignature(): any[] {
    const sig = this.getParsedSignature();
    if (!sig || !Array.isArray(sig.Inputs)) return [];
    return sig.Inputs.map((p: any) => ({
      name: p.Name,
      Typename: this.mapCsTypeToKuika(p.Type, p.Name),
      cardinality: isSapParamList(p) ? ECardinality.Many : ECardinality.One,
      symbol: { category: 0, label: "", description: "" },
      isOptionalParameter: !!p.Optional,
      realPropName: p.Name,
      displayName: p.Text || p.Name
    }));
  }

  private getParsedSignature(): any | null {
    const data = this.state.functionSignatureJson;
    if (!data) return null;
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (err) {
        return null;
      }
    }
    return data;
  }

  private getNormalizedSignature(): any | null {
    const sig = this.getParsedSignature();
    if (!sig) return null;
    return Array.isArray(sig) ? sig[0] : sig;
  }

  /** A file output's name template or MIME type, as typed under its row. */
  handleFileContentChange = (paramName: string, field: SapFileContentField, value: string): void => {
    const next: Record<string, ISapFileContentOverride> = { ...(this.state.fileContentOverrides ?? {}) };
    next[paramName] = { ...(next[paramName] ?? {}), [field]: value };
    this.setState({ fileContentOverrides: next });
  };

  /**
   * SapActionInfo JSON'una ParameterConfig enjekte eder — IsFileContent=true
   * output'lar için chunkSize / rowFieldName / sapBinarySubtype signature'dan,
   * fileNameTemplate / mimeType kullanıcı input'larından alınır. Default'lar
   * (octet-stream) input boş ise uygulanır.
   */
  private buildSapActionInfoWithParameterConfig(sig: any): any {
    const overrides = this.state.fileContentOverrides || {};
    const outputs: any[] = Array.isArray(sig?.Outputs) ? sig.Outputs : [];
    const parameterConfig: { [k: string]: any } = {};
    for (const p of outputs) {
      if (!p?.IsFileContent) continue;
      const ov = overrides[p.Name] || {};
      parameterConfig[p.Name] = {
        isFileContent: true,
        chunkSize: typeof p.ChunkSize === "number" ? p.ChunkSize : 0,
        sapBinarySubtype: p.SapBinarySubtype || "",
        rowFieldName: typeof p.RowFieldName === "string" ? p.RowFieldName : "",
        fileNameTemplate:
          (ov.fileNameTemplate && ov.fileNameTemplate.trim()) || `${p.Name?.toLowerCase?.() || "sap_file"}.bin`,
        mimeType: (ov.mimeType && ov.mimeType.trim()) || "application/octet-stream"
      };
    }
    const merged = { ...(sig || {}) };
    if (Object.keys(parameterConfig).length > 0) {
      merged.ParameterConfig = parameterConfig;
    }
    return merged;
  }

  render() {
    const signature = this.getNormalizedSignature();
    return (
      <Form ref={this.formRef} layout="vertical">
        <div className="kuika__data-diagram__create-con kuika__data-diagram__create-con--bar">
          <KuikaEditorHeader
            title={this.state.action?.name || "SAP Action"}
            backLabel="Back to Datasources"
            submitLabel={"UPDATE"}
            onSubmit={this.handleUpdateSapAction}
            status={this.state.saveStatus}
            onCancel={this.closePanel}
            renderTitle={this.renderTitle}
            cancelTestId="sapActionBackButton"
            submitTestId="sapActionSubmitButton"
          />
          <div className="kuika__data-diagram__create-con__container sap-action-container">
            <KuikaSpinnerSmall spinning={this.state.isLoading} size="small">
              <div className="kuika__data-diagram__create-con__container__section sap-action-section">
                <div className="sap-row">
                  <div className="kuika__data-diagram__create-con__container__field-item sap-field-item sap-col">
                    <KuikaSmallMediumText>Function Group</KuikaSmallMediumText>
                    <Form.Item name="sapFunctionGroup">
                      <KuikaSelectbox
                        className="sap-select"
                        placeholder="Function group"
                        searchable
                        onSearch={async (input: string) => {
                          const dsId = this.props.actionDatasource?.id;
                          if (!dsId) return;
                          if (!input || input.length < 3) {
                            if (this.fgSearchTimer) clearTimeout(this.fgSearchTimer);
                            this.setState({ functionGroups: [] });
                            return;
                          }
                          if (this.fgSearchTimer) clearTimeout(this.fgSearchTimer);
                          this.setState({ isSearchingGroups: true });
                          this.fgSearchTimer = setTimeout(async () => {
                            try {
                              const res = await DatasourceService.GetSapFunctionGroups(dsId, input);
                              const functionGroups = (res?.data || []).map((x: any) => ({
                                label: x.groupName,
                                value: x.groupName
                              }));
                              this.setState({ functionGroups });
                            } catch {}
                            this.setState({ isSearchingGroups: false });
                          }, 300);
                        }}
                        options={this.state.functionGroups}
                        value={this.state.selectedFunctionGroup}
                        disabled
                      />
                    </Form.Item>
                  </div>
                  <div className="kuika__data-diagram__create-con__container__field-item sap-field-item sap-col">
                    <KuikaSmallMediumText>Function</KuikaSmallMediumText>
                    <Form.Item name="sapFunction">
                      <KuikaSelectbox
                        className="sap-select"
                        placeholder="Function"
                        searchable
                        onSearch={async (input: string) => {
                          const dsId = this.props.actionDatasource?.id;
                          if (!dsId) return;
                          if (!input || input.length < 3) {
                            if (this.fnSearchTimer) clearTimeout(this.fnSearchTimer);
                            this.setState({ functions: [] });
                            return;
                          }
                          if (this.fnSearchTimer) clearTimeout(this.fnSearchTimer);
                          this.setState({ isSearchingFunctions: true });
                          this.fnSearchTimer = setTimeout(async () => {
                            try {
                              const res = await DatasourceService.GetSapFunctions(
                                dsId,
                                this.state.selectedFunctionGroup,
                                input
                              );
                              const functions = (res?.data || []).map((x: any) => ({
                                label: x.functionName || x.funcName || x.name,
                                value: x.functionName || x.funcName || x.name
                              }));
                              this.setState({ functions });
                            } catch {}
                            this.setState({ isSearchingFunctions: false });
                          }, 300);
                        }}
                        options={this.state.functions}
                        value={this.state.selectedFunction}
                        disabled
                      />
                    </Form.Item>
                  </div>
                </div>
                {signature && (
                  <>
                    <div className="sap-row">
                      <div className="kuika__data-diagram__create-con__container__field-item sap-field-item sap-col">
                        <KuikaSmallMediumText>Inputs</KuikaSmallMediumText>
                        <SapSignatureGrid
                          label="Inputs"
                          params={signature.Inputs}
                          emptyText="No inputs"
                          testIdPrefix="sapInputs"
                          fileContentOverrides={this.state.fileContentOverrides}
                          onFileContentChange={this.handleFileContentChange}
                        />
                      </div>
                      <div className="kuika__data-diagram__create-con__container__field-item sap-field-item sap-col">
                        <KuikaSmallMediumText>Outputs</KuikaSmallMediumText>
                        <SapSignatureGrid
                          label="Outputs"
                          params={signature.Outputs}
                          emptyText="No outputs"
                          testIdPrefix="sapOutputs"
                          fileContentOverrides={this.state.fileContentOverrides}
                          onFileContentChange={this.handleFileContentChange}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </KuikaSpinnerSmall>
          </div>
        </div>
      </Form>
    );
  }

  private handleUpdateSapAction = async (): Promise<void> => {
    if (!this.state.action) return;
    const dsId = this.props.actionDatasource?.id || this.state.action.kDatasource_Id;
    const actionId = this.props.actionId || (this.state.action.id as any);
    const actionName =
      this.formRef.current?.getFieldValue("sapFunction") || this.state.selectedFunction || this.state.action.name;
    if (!dsId || !actionId || !actionName) return;
    // The commit button is live again as soon as the ✓ replaces the spinner, so a second press
    // would re-fire the save behind the closing panel.
    if (!this.saveStatus.canStart) return;
    try {
      this.setState({ isLoading: true });
      // ensure we have freshest signature from helper
      await this.loadSignatureFromHelper();
      const params = this.buildParametersFromSignature();
      const sigObj =
        typeof this.state.functionSignatureJson === "string"
          ? (() => {
              try {
                return JSON.parse(this.state.functionSignatureJson);
              } catch {
                return {};
              }
            })()
          : (this.state.functionSignatureJson ?? {});
      const sigWithConfig = this.buildSapActionInfoWithParameterConfig(sigObj);
      const sapInfoString = JSON.stringify(sigWithConfig);
      const original = this.state.action;
      const payload: IActionDefinitionDto = {
        ...original,
        id: actionId as any,
        name: actionName,
        kDatasource_Id: dsId as any,
        kDatasource_Name: original?.kDatasource_Name || this.props.actionDatasource?.name,
        sapActionInfo: sapInfoString,
        actionParameters: params as any,
        parameters: params as any
      } as any;
      const res = await DatasourceService.SaveAction(payload);
      if (res?.status !== 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return;
      }
      await this.props.sd.loadActionDefinitions();
      // The ✓ goes up first and the panel leaves after it — see `SaveStatus`.
      this.saveStatus.succeed((): void => {
        // The list behind the panel lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: actionName, parentId: dsId });
        this.closePanel();
      });
    } catch (e) {
      this.saveStatus.fail();
    } finally {
      this.setState({ isLoading: false });
    }
  };

  checkNeedRender = (): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null))
});

const editSapAction = connect(mapStateToProps, mapDispatchToProps)(withSD<IEditSapActionProps>(EditSapAction));
export { editSapAction as EditSapAction };
